import {
  contractConfig,
  ETH,
  stableVaultContractConfig,
} from "@config/contractsConfig";
import { toFixedWithoutRounding } from "@constants/common";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { formatUnits, maxUint256 } from "viem";
import { useAccount, useReadContracts } from "wagmi";
import useDappChain from "./useDappChain";

const useStableVaultAllowance = () => {
  const { selectedERC20StableVault } = useSelector((state: any) => state.user);
  const { isTransactionProcessing } = useSelector((state: any) => state.stake);
  const { address: walletAddress } = useAccount();
  const { chain } = useDappChain();

  // Use current chain configuration
  const chainConfig = contractConfig[chain.id];

  const token = useMemo(() => {
    if (!chainConfig || !stableVaultContractConfig[chain.id]) return null;

    let token: any =
      stableVaultContractConfig[chain.id].tokens?.[selectedERC20StableVault];

    if (!token) {
      token = chainConfig.tokens[selectedERC20StableVault];
    }

    if (selectedERC20StableVault === chainConfig.xtoken.symbol) {
      token = {
        ...chainConfig.xtoken,
        tags: [],
      };
    }

    return token;
  }, [chainConfig, selectedERC20StableVault, chain.id]);

  const allowanceContracts = useMemo(() => {
    if (!token || !stableVaultContractConfig[chain.id]) return [];

    const contracts = [];
    const vaultAddress =
      stableVaultContractConfig[chain.id]?.interactionPool.address;

    contracts.push({
      ...token,
      functionName: "allowance",
      args: [walletAddress, vaultAddress],
      chainId: chain.id,
    });

    return contracts;
  }, [token, walletAddress, chain.id]);

  const minDepositContracts = useMemo(() => {
    if (!stableVaultContractConfig[chain.id]) return [];

    const contracts = [];
    const vaultConfig =
      stableVaultContractConfig[chain.id]?.interactionPool || {};

    contracts.push({
      ...vaultConfig,
      functionName: "minDepositAmount",
      chainId: chain.id,
    });

    return contracts;
  }, [chain.id]);

  const { data, refetch: refetchAllowance } = useReadContracts({
    contracts: allowanceContracts,
    query: {
      enabled: !!walletAddress && allowanceContracts.length > 0,
      refetchInterval: 5_000,
    },
  });

  const { data: minDepositData } = useReadContracts({
    contracts: minDepositContracts,
    query: {
      enabled: !!stableVaultContractConfig[chain.id],
      refetchInterval: 5_000,
    },
  });

  useEffect(() => {
    if (!isTransactionProcessing) {
      refetchAllowance();
    }
  }, [isTransactionProcessing, refetchAllowance]);

  const allowanceData = useMemo(() => {
    if (data && walletAddress) {
      return {
        erc20Allowance:
          selectedERC20StableVault !== ETH
            ? +toFixedWithoutRounding(
                data[0]?.result
                  ? formatUnits(data[0].result as bigint, token?.decimals || 18)
                  : "0",
                8
              )
            : +toFixedWithoutRounding(
                formatUnits(
                  maxUint256,
                  chainConfig?.nativeToken?.decimals || 18
                ),
                8
              ),
        minDepositAmount: minDepositData?.[0]?.result
          ? +toFixedWithoutRounding(
              formatUnits(
                minDepositData[0].result as bigint,
                token?.decimals || 18
              ),
              8
            )
          : 0,
      };
    }
    return {
      erc20Allowance: 0,
      minDepositAmount: 0,
    };
  }, [
    data,
    walletAddress,
    selectedERC20StableVault,
    token,
    chainConfig,
    minDepositData,
  ]);

  return allowanceData;
};

export default useStableVaultAllowance;
